/**
 * The partner context the Vercel Routing Middleware (`apps/crm-app/middleware.ts`)
 * injects into index.html, shared by the middleware (writer) and the SPA
 * (reader, `./current-partner.ts`).
 *
 * Why: the SPA is ONE Vite build served on every partner host. Any partner
 * list compiled into it ships to every partner, so a partner could read the
 * names, domains and logos of all the others. The registries
 * (`./partners.ts`, `./reseller-partners.ts`, the partner brand files) are
 * therefore read by the middleware only, which resolves the request host and
 * writes the ONE partner that host needs into the page.
 *
 * Types and constants only: no partner data here. Relative imports only (the
 * middleware is bundled by Vercel apart from Vite).
 */

import type { BrandConfig } from './brands/types';
import type { ResellerPartnerConfig } from './reseller-partners';

/** A partner as the SPA sees it: the registry entry + its code brand, if any. */
export interface InjectedPartner {
  /** Brand / signup-attribution key (the partner slug). */
  brand: string;
  rootDomain: string;
  appHost: string;
  clerkFapi: string;
  dashboardHost?: string;
  brandSource?: 'code' | 'db';
  /** The partner's code brand file; absent for DB-branded partners. */
  brandConfig?: BrandConfig;
}

/**
 * What the middleware writes on `window`:
 *  - `partner`: the partner serving the current host. Partner hosts only.
 *  - `redirectPartner`: on Tagada hosts, the partner whose app or dashboard
 *    origin is the `redirect_url` of the request (sign-in bounce from a
 *    partner satellite); null otherwise.
 *  - `resellers`: reseller-only partners (org tag on the Tagada domain). All of
 *    them on Tagada hosts; on a partner host, only that partner's own entry.
 */
export interface PartnerContext {
  partner: InjectedPartner | null;
  redirectPartner: InjectedPartner | null;
  resellers: Record<string, ResellerPartnerConfig>;
}

export const PARTNER_GLOBAL = '__TAGADA_PARTNER__';
export const REDIRECT_PARTNER_GLOBAL = '__TAGADA_REDIRECT_PARTNER__';
export const RESELLERS_GLOBAL = '__TAGADA_RESELLERS__';

/**
 * Tagada hosts only: `GET /__partner/redirect?url=<url>` answers
 * `{ partner: InjectedPartner | null }` for a redirect target the middleware
 * could not see (Clerk's `#/?redirect_url=` form never reaches the server).
 * 404 on partner hosts.
 */
export const REDIRECT_PARTNER_ENDPOINT = '/__partner/redirect';

declare global {
  interface Window {
    __TAGADA_PARTNER__?: InjectedPartner | null;
    __TAGADA_REDIRECT_PARTNER__?: InjectedPartner | null;
    __TAGADA_RESELLERS__?: Record<string, ResellerPartnerConfig>;
  }
}

/** `https://<appHost>` and `https://<dashboardHost>`: the partner's own origins. */
export const partnerOrigins = (p: Pick<InjectedPartner, 'appHost' | 'dashboardHost'>): string[] =>
  p.dashboardHost ? [`https://${p.appHost}`, `https://${p.dashboardHost}`] : [`https://${p.appHost}`];

/** True if `host` is `rootDomain` itself or a subdomain of it. */
export const hostBelongsToRoot = (host: string, rootDomain: string): boolean =>
  host === rootDomain || host.endsWith('.' + rootDomain);
